import { useMemo } from "react";
import {
  LineChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

/**
 * JobsTrendChart — Chart 2.
 *
 * Two lines only: Job Cards Created and Job Cards Completed.
 * No revenue, no bars, no forecasts (per spec).
 */
export function JobsTrendChart({ series = [], days = 7 }) {
  const data = useMemo(
    () =>
      (series || []).map((r) => ({
        ...r,
        // short label like "Aug 5"
        label: new Date(r.date).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        }),
      })),
    [series]
  );

  const totalCreated = data.reduce((s, r) => s + (r.created || 0), 0);
  const totalCompleted = data.reduce((s, r) => s + (r.completed || 0), 0);

  return (
    <section className="bg-white rounded-xl border border-[#E4E7EC] p-5" data-testid="chart-jobs-trend">
      <header className="mb-2 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-[15px] font-semibold text-[#1d3557]">Job Cards Created</h3>
          <p className="text-[11.5px] text-[#667085] mt-0.5">
            Incoming vs completed workload over the last {days} days.
          </p>
        </div>
        <div className="hidden sm:flex items-center gap-4 text-[11.5px]">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-[#e63946]" />
            <span className="text-[#667085]">Created</span>
            <span className="font-mono tabular-nums font-semibold text-[#1d3557]">{totalCreated}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-[#12B76A]" />
            <span className="text-[#667085]">Completed</span>
            <span className="font-mono tabular-nums font-semibold text-[#1d3557]">{totalCompleted}</span>
          </div>
        </div>
      </header>

      <div className="h-[240px]" data-testid="jobs-trend-chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 10, left: -12, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E4E7EC" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: "#667085" }}
              tickLine={false}
              axisLine={{ stroke: "#E4E7EC" }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#667085" }}
              allowDecimals={false}
              tickLine={false}
              axisLine={{ stroke: "#E4E7EC" }}
              width={30}
            />
            <Tooltip
              contentStyle={{
                background: "#1d3557",
                border: "none",
                borderRadius: 6,
                color: "white",
                fontSize: 12,
                padding: "6px 10px",
              }}
              itemStyle={{ color: "white" }}
              labelStyle={{ color: "#a8dadc", marginBottom: 2 }}
            />
            <Line
              type="monotone"
              dataKey="created"
              name="Created"
              stroke="#e63946"
              strokeWidth={2}
              dot={{ r: 3, stroke: "#e63946", fill: "white", strokeWidth: 2 }}
              activeDot={{ r: 5 }}
            />
            <Line
              type="monotone"
              dataKey="completed"
              name="Completed"
              stroke="#12B76A"
              strokeWidth={2}
              dot={{ r: 3, stroke: "#12B76A", fill: "white", strokeWidth: 2 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
